import { useEffect, Fragment } from "react";
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import * as amplitude from "@amplitude/analytics-browser";

import { CircleExclamation } from "libs/design-system/src/assets/Icons"
import {
  PageStatus,
  SkeletonLoader
} from "@unified-checkout/design-system";

import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { MOBILE_BREAKPOINT } from "@app/dashboard/constants/break-points";
import {
  TRANSACTIONS_EVENTS,
  TRANSACTIONS_EVENTS_PARAMS
} from "@app/dashboard/constants/tracking_events/transactions";
import { useLangStore } from "@app/dashboard/store/lang";
import { useScreenWidth } from "@app/dashboard/hooks/useScreenWidth";
import { getSingleTransactionDetails } from "@app/dashboard/services/inner_layout/transactions/single-transaction";

import { Header } from "./Header";
import Timeline from "./sections/Timeline";
import Details from "./sections/Details";
import CustomerDetails from "./sections/CustomerDetails";
import CheckoutSummary from "./sections/CheckoutSummary";
import Breakdown from "./sections/Breakdown";
import PaymentMethods from "./sections/PaymentMethods";
import InstallmentInfo from "./sections/InstallmentInfo";
import Installment from "./sections/Installment";

import styles from "./single-transaction.module.scss";

export const SingleTransaction = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const screenWidth = useScreenWidth();
  const { lang } = useLangStore();
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const {
    isLoading,
    isError,
    data: response,
    refetch,
    isFetching
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_SINGLE_TRANSACTION_DETAILS, lang],
    queryFn: () => getSingleTransactionDetails(id as string),
    enabled: !!id,
    refetchOnMount: "always",
  });

  const singleTransactionDetails = response?.data?.data;

  useEffect(() => {
    if (singleTransactionDetails) {
      const { id, details, source_data } = singleTransactionDetails;
      const { order_id, type } = details || {};
      const { tokenized_transaction } = source_data || {};

      amplitude.track(TRANSACTIONS_EVENTS.TRNX_DETAILS_PAGE_VIEWED, {
        [TRANSACTIONS_EVENTS_PARAMS.TRNX_ID]: id,
        [TRANSACTIONS_EVENTS_PARAMS.ORDER_ID]: order_id.replace('#', ''),
        [TRANSACTIONS_EVENTS_PARAMS.TRNX_TYPE]: type,
        [TRANSACTIONS_EVENTS_PARAMS.IS_TOKENIZED]: !!tokenized_transaction,
      });
    }
  }, [response]);

  const {
    break_down,
    checkout_summary,
    timeline,
    customer,
    details,
    payment_method,
    installment_info,
    installment,
    customer_info
  } = singleTransactionDetails || {};

  const detailsBoxRenderer = (
    {
      box = undefined,
      height,
      noOfRows,
      key
    }: {
      box: ReactNode,
      height: string,
      noOfRows: number,
      key: number | string
    }
  ): ReactNode => {
    return (
      isLoading || isFetching ?
        <SkeletonLoader
          type="bordered_box"
          noOfRows={noOfRows}
          height={height}
          key={key}
        />
        :
        box && <Fragment key={key}>{box}</Fragment>
    )
  };

  const allBoxes = [
    detailsBoxRenderer({
      box: (timeline && false) && <Timeline />,
      noOfRows: 7,
      height: "24rem",
      key: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.timeline.title')
    }),
    detailsBoxRenderer({
      box: details && <Details singleTransactionDetails={singleTransactionDetails}/>,
      noOfRows: 6,
      height: "20rem",
      key: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.title')
    }),
    detailsBoxRenderer({
      box: checkout_summary && <CheckoutSummary checkout_summary={checkout_summary}/>,
      noOfRows: 7,
      height: "24rem",
      key: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.checkoutSummary.title')
    }),
    detailsBoxRenderer({
      box: payment_method && <PaymentMethods id={id as string} singleTransactionDetails={singleTransactionDetails}/>,
      noOfRows: 6,
      height: "20rem",
      key: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.paymentMethod.title')
    }),
    detailsBoxRenderer({
      box: break_down && <Breakdown singleTransactionDetails={singleTransactionDetails} />,
      noOfRows: 7,
      height: "24rem",
      key: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.transactionBreakdown.title')
    }),
    detailsBoxRenderer({
      box: customer && <CustomerDetails customerInfo={customer_info}/>,
      noOfRows: 6,
      height: "20rem",
      key: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.customer.title')
    }),
    detailsBoxRenderer({
      box: installment_info && <InstallmentInfo installmentInfo={installment_info} />,
      noOfRows: 7,
      height: "24rem",
      key: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.installmentInfo.title')
    }),
    detailsBoxRenderer({
      box: installment && <Installment installment={installment} />,
      noOfRows: 7,
      height: "24rem",
      key: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.installment.title')
    })
  ];
  // Filter out empty boxes
  const filteredBoxes = allBoxes.filter(Boolean);

  // Split the boxes into two columns
  const col1Boxes = filteredBoxes.filter((_, index) => index % 2 === 0); // Even-indexed items
  const col2Boxes = filteredBoxes.filter((_, index) => index % 2 !== 0); // Odd-indexed items

  const content = <>
    {
      isMobile ?
        <>
          {col1Boxes}
          {col2Boxes}
        </>
        :
        [col1Boxes, col2Boxes].map((colContent, index) =>
          <div className={styles["single-transaction__main__col"]} key={`single-transaction-col-${index}`}>
            {colContent}
          </div>
        )
    }
  </>;

  return (
    <div className={styles["single-transaction"]}>
      {
        isError ?
          <PageStatus
            status="error"
            title={t('innerLayout.errors.smthWentWrong')}
            description={t('innerLayout.errors.dataRetrievalErr')}
            icon={CircleExclamation}
          />
          :
          <>
            <Header
              id={Number(id)}
              data={singleTransactionDetails}
              isLoading={isLoading || isFetching}
              isError={isError}
              refetchData={refetch}
            />

            <div className={styles["single-transaction__main"]}>
              {content}
            </div>
          </>
      }
    </div>
  )
}